import React, { useEffect, useState } from "react";
import { ArrowLeftRight, MonitorSmartphone } from "lucide-react";
import { WarningModal } from "../../../../components/WarningModal/WarningModal";
import { Input } from "../../../../components/Core/Form/component";
import { sendBillToSessionService } from "../../../../services/tableBills";
import { getServiceError } from "../../../../utils/getServiceError";

interface SendToSessionModalProps {
  open: boolean;
  onClose: () => void;
  saleUuid: string;
  saleCode: string;
  /** Name of the open bill the order is already in; when set, the modal moves that bill to another session. */
  currentBillName?: string;
  /** Called with the API message once the order sits in a bill of the session. */
  onSent: (message: string) => Promise<void>;
}

/**
 * YOSHI-11055: sends a pending external order to a terminal session so that terminal charges it, or moves the
 * open bill it is already in to another session, for an order sent to the wrong terminal. Both go through the
 * same endpoint. The session name repeats across users, so it is asked for together with the username.
 */
export const SendToSessionModal: React.FC<SendToSessionModalProps> = ({
  open,
  onClose,
  saleUuid,
  saleCode,
  currentBillName,
  onSent,
}) => {
  const isMove: boolean = !!currentBillName;
  const [username, setUsername] = useState<string>("");
  const [sessionName, setSessionName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);

  // A previous attempt's values and error must not carry over to the next opening
  useEffect(() => {
    if (open) {
      setUsername("");
      setSessionName("");
      setErrorMessage("");
    }
  }, [open]);

  const handleConfirm = async (): Promise<void> => {
    if (username.trim() === "" || sessionName.trim() === "") {
      setErrorMessage("Captura el usuario y la sesión de la terminal.");
      return;
    }

    setIsSending(true);
    setErrorMessage("");
    try {
      const response = await sendBillToSessionService(saleUuid, {
        username: username.trim(),
        session_name: sessionName.trim(),
      });
      await onSent(response.message);
    } catch (error) {
      // The modal stays open with the reason (session not found, not active...) so it can be corrected here
      setErrorMessage(getServiceError(error));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <WarningModal
      open={open}
      onClose={onClose}
      title={isMove ? "Cambiar de sesión" : "Mandar a sesión"}
      messageTitle={isMove ? "Cobrar en otra terminal" : "Cobrar en una terminal"}
      message={
        isMove ? (
          <p>
            La cuenta <span className="font-bold text-black">{currentBillName}</span> de
            la orden <span className="font-bold text-black">{saleCode}</span> se moverá
            a la sesión que indiques y dejará de aparecer en la terminal donde está.
            Solo se puede mientras no tenga pagos.
          </p>
        ) : (
          <p>
            La orden <span className="font-bold text-black">{saleCode}</span> se
            mandará como cuenta a la sesión que indiques, para cobrarla en esa
            terminal.
          </p>
        )
      }
      additionalContent={
        <div className="flex flex-col gap-3">
          <Input
            label="Usuario"
            placeholder="Nombre de usuario de la terminal"
            value={username}
            onChange={(value: string) => setUsername(value)}
          />
          <Input
            label="Sesión"
            placeholder="Nombre de la sesión (normal o permanente)"
            value={sessionName}
            onChange={(value: string) => setSessionName(value)}
          />
          {errorMessage !== "" && (
            <p className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
        </div>
      }
      confirmLabel={isMove ? "Cambiar de sesión" : "Mandar a sesión"}
      ConfirmIcon={isMove ? ArrowLeftRight : MonitorSmartphone}
      onConfirm={handleConfirm}
      isLoading={isSending}
    />
  );
};
